import * as Yup from 'yup';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ReferentialKey } from 'api/referential';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { BaseSection } from 'components/layout/Section/BaseSection';
import { GenericMessage } from 'components/feedback/Message';
import { FormikDropdown } from 'components/forms/formik/FormikDropdown';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { getMultipleReferentialsByKeys } from 'features/referentials/slices/getMultipleReferentialsByKeysThunk';
import { selectReferentialByKey } from 'features/referentials/selectors/selectReferentialByKey';
import { usePanel } from 'hooks/usePanel';
import { frenchMobileRule, normalizeFrenchMobile } from 'shared/utils/frenchMobile';
import {
  AccessMember,
  GerantWithoutAccess,
  declareGerant,
  getPersonneMoraleMembers,
} from '../../api/personneMoraleAccessApi';

interface DeclareGerantFormValues {
  accessId: string;
  collaboratorId: string;
  civiliteId: string;
  phone: string;
}

export interface DeclareGerantPanelProps {
  personneMoraleId: string;
  /** Preselected access — the current user's when they declare themselves. */
  defaultAccessId?: string;
  /**
   * Whether the mobile is mandatory: true from the mandate card (signing follows right
   * away), false from "My team" (the mandate sending will ask for it if missing).
   */
  phoneRequired?: boolean;
  onSuccess?: () => void;
}

const NO_COLLABORATOR = '';

/**
 * Declares one of the firm's accesses as its (legal) manager: the natural person who
 * signs the direct-debit mandates the firm pays for. Reserved to the firm's owner.
 *
 * Either the person is created from the account (name, first name, email) with their
 * civility and mobile, or a manager already entered without an account when the company
 * was created gets attached to the access. Same panel from "My team" and from the
 * mandate card. See dox core/mandates/back/cabinet-signatory.
 */
export const DeclareGerantPanel: React.FC<DeclareGerantPanelProps> = ({
  personneMoraleId,
  defaultAccessId,
  phoneRequired = false,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const { setPanelActions, closePanel } = usePanel();
  const formikRef = useRef<FormikProps<DeclareGerantFormValues>>(null);

  const civilites = useAppSelector(selectReferentialByKey(ReferentialKey.Civility)) ?? [];
  useEffect(() => {
    if (civilites.length === 0) {
      dispatch(getMultipleReferentialsByKeys([ReferentialKey.Civility]));
    }
    // Load once: the empty list at mount time is enough to decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const [members, setMembers] = useState<AccessMember[]>([]);
  const [gerantsWithoutAccess, setGerantsWithoutAccess] = useState<GerantWithoutAccess[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setLoadFailed(false);
    getPersonneMoraleMembers(personneMoraleId)
      .then((data) => {
        if (cancelled) return;
        setMembers(data.collaborators.filter((m) => m.isActive && !m.isGerant));
        setGerantsWithoutAccess(data.gerantsWithoutAccess ?? []);
      })
      .catch((e) => {
        console.error('Chargement des accès impossible', e);
        if (cancelled) return;
        setLoadFailed(true);
        toasterService.showToast({
          message: 'Chargement des accès du cabinet impossible.',
          displayType: DisplayTypeEnum.Toaster,
          color: GenericColors.Red,
        });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [personneMoraleId]);

  const accessItems = useMemo(
    () => members.map((m) => ({ key: m.accessId, label: `${m.displayName} · ${m.email}` })),
    [members],
  );
  const gerantItems = useMemo(
    () => [
      { key: NO_COLLABORATOR, label: 'Créer sa fiche depuis son compte' },
      ...gerantsWithoutAccess.map((g) => ({ key: g.collaboratorId, label: g.displayName })),
    ],
    [gerantsWithoutAccess],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        accessId: Yup.string().required('Choisissez le collaborateur à déclarer gérant'),
        collaboratorId: Yup.string(),
        civiliteId: Yup.string().when('collaboratorId', {
          is: (v: string) => !v,
          then: (schema) => schema.required('La civilité est requise'),
          otherwise: (schema) => schema,
        }),
        phone: frenchMobileRule(
          'Un mobile français (06 ou 07) est attendu',
          phoneRequired ? 'Le mobile est requis : il reçoit le code de signature' : undefined
        ),
      }),
    [phoneRequired],
  );

  useEffect(() => {
    setPanelActions({
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.VALIDATE',
      // No selectable access: the accessId field is not rendered, so a submit would
      // fail on an error nobody can see.
      submitDisabled: !isFormValid || isLoading || isSubmitting || accessItems.length === 0,
      onSubmit: () => formikRef.current?.submitForm(),
    });
  }, [setPanelActions, isFormValid, isLoading, isSubmitting, accessItems.length]);

  const handleSubmit = async (values: DeclareGerantFormValues) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await declareGerant(values.accessId, {
        civiliteId: values.collaboratorId ? null : values.civiliteId,
        phone: values.phone ? normalizeFrenchMobile(values.phone) : null,
        collaboratorId: values.collaboratorId || null,
      });
      if (!res.isSuccess) {
        toasterService.showToast({
          message: res.errorMessage ?? 'Déclaration du gérant impossible.',
          displayType: DisplayTypeEnum.Toaster,
          color: GenericColors.Red,
        });
        return;
      }
      toasterService.showToast({
        message: 'Gérant déclaré : il peut signer les mandats du cabinet.',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Green,
      });
      onSuccess?.();
      closePanel();
    } catch (e) {
      console.error(e);
      toasterService.showToast({
        message: 'Déclaration du gérant impossible.',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Never substitute another member for a requested one: if the clicked member is not
  // selectable (inactive, already manager), the owner must pick explicitly.
  const initialAccessId = defaultAccessId
    ? members.some((m) => m.accessId === defaultAccessId) ? defaultAccessId : ''
    : members.length === 1 ? members[0].accessId : '';

  return (
    <Formik<DeclareGerantFormValues>
      enableReinitialize
      validateOnMount
      initialValues={{
        accessId: initialAccessId,
        collaboratorId: NO_COLLABORATOR,
        civiliteId: '',
        phone: '',
      }}
      validationSchema={validationSchema}
      innerRef={formikRef}
      onSubmit={handleSubmit}
    >
      {({ isValid, values }) => {
        if (isValid !== isFormValid) setIsFormValid(isValid);
        const linkingExisting = !!values.collaboratorId;
        return (
          <Form>
            <GenericMessage
              type="info"
              dismissible={false}
              message="Le gérant est la personne habilitée à engager le cabinet : c'est lui qui signe les mandats de prélèvement quand le cabinet règle les cotisations. Son mobile reçoit le code de signature."
            />

            <BaseSection title="Collaborateur à déclarer gérant">
              <div className="col-span-6 mt-2">
                {isLoading ? (
                  <p className="text-sm text-gray-500">Chargement des accès…</p>
                ) : loadFailed ? (
                  <p className="text-sm text-red-600">
                    Les accès du cabinet n'ont pas pu être chargés. Fermez le panneau et réessayez.
                  </p>
                ) : accessItems.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    Tous les accès actifs du cabinet sont déjà déclarés gérants.
                  </p>
                ) : (
                  <FormikDropdown
                    name="accessId"
                    label="Collaborateur"
                    required
                    items={accessItems}
                    disabled={isSubmitting}
                  />
                )}
              </div>
            </BaseSection>

            {gerantsWithoutAccess.length > 0 && (
              <BaseSection title="Gérant déjà saisi sans compte">
                <div className="col-span-6 mt-2">
                  <FormikDropdown
                    name="collaboratorId"
                    label="Rattacher ce compte à un gérant existant"
                    items={gerantItems}
                    disabled={isSubmitting}
                  />
                </div>
              </BaseSection>
            )}

            <BaseSection title="Identité du signataire">
              <>
                {!linkingExisting && (
                  <div className="col-span-3 mt-2">
                    <FormikDropdown
                      name="civiliteId"
                      label="Civilité"
                      required
                      items={civilites}
                      disabled={isSubmitting}
                    />
                  </div>
                )}
                <div className={`${linkingExisting ? 'col-span-6' : 'col-span-3'} mt-2`}>
                  <FormikInputText
                    name="phone"
                    label="Mobile"
                    type="tel"
                    required={phoneRequired}
                    placeholder="06 12 34 56 78"
                    disabled={isSubmitting}
                  />
                </div>
                {!linkingExisting && (
                  <p className="col-span-6 text-xs text-gray-500">
                    Nom, prénom et email sont repris du compte du collaborateur.
                  </p>
                )}
              </>
            </BaseSection>
          </Form>
        );
      }}
    </Formik>
  );
};
